import React, { memo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ViewOddsButton from '../containers/ViewOddsButton';
import '../css/ViewOddsPlayerGroup.scss';
import { MARKET_TYPE_GROUPS_ID } from 'constants/appConfig';
import OddsButton from 'ui/Button';
import { detectMobile } from 'utils/UiHelper';

const className = 'ViewOddsPlayerGroup';

const ViewOddsPlayerGroup = (props) => {
    const [expanded, changeExpanded] = useState(false);
    const [players, setPlayers] = useState([]);

    const labels = getUniqColumnLabels(props);

    if (labels.length > props.columnLimit) {
        labels.length = props.columnLimit;
    }

    useEffect(
        () => {
            setPlayers(
                getUniqPlayerList(
                    props.marketList,
                    props.rowKey,
                    props.allMarkets
                )
            );
        },
        [props.allMarkets, props.marketList, props.rowKey]
    );

    const playerBetList = composeBetsByPlayer(players, props);

    const handleShowMoreBtnClick = () => changeExpanded(true);
    if (playerBetList.length > props.rowLimit && !expanded) {
        playerBetList.length = props.rowLimit;
    }
    return (
        <div className={className}>
            {detectMobile() && (
                <div className="_row">
                    <div className="_cell _grow" />
                    {labels.map((label) => {
                        const uiLabel = props.labelFormatter(label);
                        return (
                            <div key={label} className="_cell">
                                <div className="_column-label">{uiLabel}</div>
                            </div>
                        );
                    })}
                </div>
            )}

            {playerBetList.map(({ player, bets }) => {
                return (
                    <div key={player} className="_row">
                        <div className="_cell _grow">
                            <div className="_btn-placeholder">{player}</div>
                        </div>
                        {labels.map((label, index) => {
                            const { bet, market } = bets[label] || {};
                            if (!bet || !market) {
                                return <div key={index} className="_cell" />;
                            }
                            const uiLabel = props.labelFormatter(label);
                            return (
                                <div key={label} className="_cell">
                                    <ViewOddsButton
                                        isOnlyOdds={detectMobile()}
                                        label={uiLabel}
                                        item={bet}
                                        market={market}
                                    />
                                </div>
                            );
                        })}
                    </div>
                );
            })}

            {!expanded && (
                <div className="_control-container">
                    <OddsButton active onClick={handleShowMoreBtnClick}>
                        Vis alle
                    </OddsButton>
                </div>
            )}
        </div>
    );
};

/**
 * @param  {object[]} marketList
 * @param  {object[]} allMarkets
 * @param  {String} rowKey
 * @return {String[]}
 */
function getUniqPlayerList(marketList, rowKey, allMarkets) {
    return [
        ...new Set(
            marketList
                .reduce((acc, item) => [...acc, ...item.itemList], [])
                .map((item) => item[rowKey])
                .filter((item) => {
                    let shouldRender = false;
                    if (allMarkets) {
                        allMarkets.forEach((market) => {
                            if (
                                market.itemList.some(
                                    (el) => el.odds > 1 && el.bet === item
                                )
                            ) {
                                shouldRender = true;
                            }
                        });
                    } else {
                        shouldRender = true;
                    }
                    return shouldRender;
                })
        ),
    ];
}

/**
 * @param {Object} props
 * @return {String[]}
 */
function getUniqColumnLabels(props) {
    if (
        props.marketList.length &&
        props.marketList[0].marketTypeGroup ===
            MARKET_TYPE_GROUPS_ID.SPILLER_SPECIALS.MAL
    ) {
        return ['1+', '2+', '3+'];
    } else {
        return [
            ...new Set(
                props.marketList
                    .map((market) => market[props.columnKey])
                    .filter(props.filterColumn)
            ),
        ];
    }
}

/**
 * @typedef {Object} PlayerBetItem
 * @property {String} player
 * @property {Object} bets
 * @property {MarketItemModel} bets[String].bet
 * @property {MarketModel} bets[String].market
 *
 * @param {String[]} players
 * @param {Object} props
 * @return {PlayerBetItem[]}
 */
function composeBetsByPlayer(players, props) {
    return players.map((player) => ({
        player,
        bets: {
            ...props.marketList.reduce((acc, market) => {
                if (
                    props.marketList.length &&
                    props.marketList[0].marketTypeGroup ===
                        MARKET_TYPE_GROUPS_ID.SPILLER_SPECIALS.MAL
                ) {
                    market.itemList.map((item) => {
                        if (item.bet === player) {
                            acc[item.betNr + '+'] = {
                                bet: item,
                                market: market,
                            };
                        }
                    });
                } else {
                    if (props.filterColumn(market[props.columnKey])) {
                        acc[market[props.columnKey]] = {
                            bet: market.itemList.find(
                                (item) => item[props.rowKey] === player
                            ),
                            market: market,
                        };
                    }
                }
                return acc;
            }, {}),
        },
    }));
}

ViewOddsPlayerGroup.defaultProps = {
    allMarkets: null,
    columnKey: 'marketTypeName',
    rowKey: 'bet',
    labelFormatter: (label) => label,
    rowLimit: 10,
    columnLimit: detectMobile() ? 4 : 5,
    marketList: [],
    filterColumn: (label) => label,
};

ViewOddsPlayerGroup.propTypes = {
    allMarkets: PropTypes.arrayOf(PropTypes.object),
    rowKey: PropTypes.string,
    columnKey: PropTypes.string,
    labelFormatter: PropTypes.func,
    columnLimit: PropTypes.number,
    rowLimit: PropTypes.number,
    marketList: PropTypes.arrayOf(PropTypes.object),
    filterColumn: PropTypes.func,
};

export default memo(ViewOddsPlayerGroup);
